import {ErrorHandlerOptions, SentryErrorHandler} from '@sentry/angular';
import {IziviaPlatformService} from '../services/common/iziviaPlatform.service';
import {IziviaReloadPermissionService} from '../services/common/iziviaReloadPermission.service';
import {HttpErrorResponse} from '@angular/common/http';

export class IziviaErrorHandler extends SentryErrorHandler {

    constructor(private readonly platformService: IziviaPlatformService,
        private readonly reloadPermissionService: IziviaReloadPermissionService,
        options?: ErrorHandlerOptions) {
        super(options);
    }

    handleError(error: unknown): void {
        /**
         * We never report HttpErrorResponse to Sentry because 4xx HTTP errors are client side errors that should not require
         * any investigation and 5xx HTTP errors are server side errors that should be reported server side
         *
         * This guard is required here (in addition to the `beforeSend` hook within `IziviaErrorErrorReportingService`) because
         * there is an internal issue in Sentry SDK that duplicate HttpErrorResponse event
         *
         * @see https://izivia.atlassian.net/browse/SMBP-22302
         */
        if (error instanceof HttpErrorResponse) {
            console.debug('[IziviaErrorHandler] HttpErrorResponse has been caught and will not be handled.', error.error);
            return;
        }

        if (this.couldBeNetworkConnectionIssue(error)) {
            console.debug('[IziviaErrorHandler] Captured error looks like a network connection issue', error);

            this.reloadPermissionService.canReloadApp()
                .subscribe(canReloadApp => {
                    if (canReloadApp) {
                        this.platformService.reload();
                    } else {
                        super.handleError(error);
                    }
                });
        } else {
            super.handleError(error);
        }
    }

    private couldBeNetworkConnectionIssue(error: unknown): boolean {
        if (error instanceof Error) {
            const chunkFailedMessage = /Loading chunk [\d]+ failed/;
            if (chunkFailedMessage.test(error.message)) {
                return true;
            }
        }

        return false;
    }
}

export function createIziviaErrorHandler(platformService: IziviaPlatformService,
    reloadPermissionService: IziviaReloadPermissionService): IziviaErrorHandler {
    return new IziviaErrorHandler(platformService, reloadPermissionService, {showDialog: false});
}
